import { useCallback, useRef, useState } from 'react';

/*
    🔥 COLA DE SUBIDA

    ⚠️ ESTO NO ES FieldFile

    FieldFile es un CAMPO: elegís archivos y quedan en el formulario
    hasta que se envía. Esto es un GESTOR: cada archivo sube por su
    cuenta, con su propio progreso, y podés cancelar o reintentar uno
    sin tocar los otros.

    ⚠️ EL ID NO ES EL NOMBRE DEL ARCHIVO

    Dos archivos pueden llamarse igual —"captura.png" de dos carpetas
    distintas— y con el nombre como clave React los pisa entre sí. El
    id es un contador propio.

    ⚠️ LA VALIDACIÓN CORRE ANTES DE ENCOLAR

    Un archivo de 80 MB en una cuenta con límite de 10 no debería
    subir un solo byte para después fallar. Se rechaza al agregarlo,
    con el motivo a la vista.

    onUpload recibe (file, { onProgress, signal }) y devuelve una
    promesa. El signal es un AbortSignal: si lo respetás, el botón de
    cancelar corta la petición de verdad.
*/

const STATUS = { PENDING: 'pending', UPLOADING: 'uploading', DONE: 'done', ERROR: 'error', CANCELED: 'canceled' };

export const UPLOAD_STATUS = STATUS;

export const onFormatBytes = (bytes) => {

    if (!bytes) return '0 B';

    const UNITS = ['B', 'KB', 'MB', 'GB'];
    const I = Math.min(UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));

    return `${(bytes / 1024 ** I).toFixed(I === 0 ? 0 : 1)} ${UNITS[I]}`;
};

export const useUploader = ({
    onUpload = null,
    maxSize = null,              // en bytes
    accept = null,               // ['image/png', '.pdf']
    maxFiles = null,
    isAuto = true,               // arranca a subir apenas se agrega
} = {}) => {

    const [useItems, setItems] = useState([]);

    const REF__useRefId = useRef(0);
    const REF__useRefControllers = useRef(new Map());

    /*
        🔥 ESPEJO DE LOS ITEMS

        ⚠️ NO SE PUEDEN DISPARAR SUBIDAS DENTRO DE setItems(prev => …)

        En React 18 con StrictMode, el updater se invoca DOS veces en
        desarrollo para detectar impurezas. Si el efecto viviera ahí,
        cada archivo subiría dos veces —y contra un backend real, eso
        es un registro duplicado por cada alta—.

        El ref permite leer el estado actual sin pasar por el updater,
        así onAdd y onRetry quedan puros y los efectos suceden después.
    */
    const REF__useRefItems = useRef([]);
    REF__useRefItems.current = useItems;

    const CB__onPatch = useCallback((id, patch) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    }, []);

    // -------------------- 🎯 VALIDACIÓN
    const CB__onValidate = useCallback((file, currentCount) => {

        if (maxFiles && currentCount >= maxFiles) return `Máximo ${maxFiles} archivos.`;

        if (maxSize && file.size > maxSize) return `Supera el máximo de ${onFormatBytes(maxSize)}.`;

        if (accept?.length) {
            const NAME = file.name.toLowerCase();
            const OK = accept.some((rule) => (
                rule.startsWith('.')
                    ? NAME.endsWith(rule.toLowerCase())
                    : file.type === rule || (rule.endsWith('/*') && file.type.startsWith(rule.slice(0, -1)))
            ));
            if (!OK) return 'Tipo de archivo no permitido.';
        }

        return null;
    }, [accept, maxSize, maxFiles]);

    // -------------------- 🎯 SUBIDA
    const CB__onStart = useCallback(async (item) => {

        if (typeof onUpload !== 'function') {
            CB__onPatch(item.id, { status: STATUS.DONE, progress: 100 });
            return;
        }

        const CONTROLLER = new AbortController();
        REF__useRefControllers.current.set(item.id, CONTROLLER);

        CB__onPatch(item.id, { status: STATUS.UPLOADING, progress: 0, error: null });

        try {

            const RESULT = await onUpload(item.file, {
                signal: CONTROLLER.signal,
                onProgress: (percent) => CB__onPatch(item.id, { progress: Math.round(percent) }),
            });

            CB__onPatch(item.id, { status: STATUS.DONE, progress: 100, result: RESULT ?? null });

        } catch (err) {

            const WAS_CANCELED = CONTROLLER.signal.aborted || err?.name === 'AbortError';

            CB__onPatch(item.id, {
                status: WAS_CANCELED ? STATUS.CANCELED : STATUS.ERROR,
                error: WAS_CANCELED ? null : (err?.message ?? 'No se pudo subir.'),
            });

        } finally {
            REF__useRefControllers.current.delete(item.id);
        }

    }, [onUpload, CB__onPatch]);

    // -------------------- 🎯 ALTA
    const CB__onAdd = useCallback((fileList) => {

        const FILES = Array.from(fileList ?? []);

        if (!FILES.length) return;

        const CURRENT = REF__useRefItems.current;

        let valid = CURRENT.filter((i) => i.status !== STATUS.ERROR).length;

        const CREATED = FILES.map((file) => {

            const ERROR = CB__onValidate(file, valid);

            if (!ERROR) valid += 1;

            return {
                id: `up_${REF__useRefId.current += 1}`,
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                /*
                    🔥 La miniatura sale de un object URL, no de un
                       FileReader: no carga el archivo entero en memoria.
                       Se libera en onRemove y en onClear.
                */
                preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
                progress: 0,
                status: ERROR ? STATUS.ERROR : STATUS.PENDING,
                error: ERROR,
                result: null,
            };
        });

        // 🔥 El updater es puro: solo concatena
        setItems((prev) => [...prev, ...CREATED]);

        // 🔥 Los efectos, después y una sola vez
        if (isAuto) CREATED.filter((i) => !i.error).forEach(CB__onStart);

    }, [CB__onValidate, isAuto, CB__onStart]);

    const CB__onCancel = useCallback((id) => {
        REF__useRefControllers.current.get(id)?.abort();
    }, []);

    const CB__onRetry = useCallback((id) => {
        const ITEM = REF__useRefItems.current.find((i) => i.id === id);
        if (ITEM) CB__onStart(ITEM);
    }, [CB__onStart]);

    const CB__onRemove = useCallback((id) => {

        REF__useRefControllers.current.get(id)?.abort();

        const ITEM = REF__useRefItems.current.find((i) => i.id === id);
        if (ITEM?.preview) URL.revokeObjectURL(ITEM.preview);

        setItems((prev) => prev.filter((i) => i.id !== id));

    }, []);

    const CB__onClear = useCallback(() => {

        REF__useRefItems.current.forEach((i) => {
            REF__useRefControllers.current.get(i.id)?.abort();
            if (i.preview) URL.revokeObjectURL(i.preview);
        });

        setItems([]);

    }, []);

    const CB__onStartAll = useCallback(() => {
        useItems.filter((i) => i.status === STATUS.PENDING).forEach(CB__onStart);
    }, [useItems, CB__onStart]);

    const TOTAL = useItems.length;
    const DONE = useItems.filter((i) => i.status === STATUS.DONE).length;
    const UPLOADING = useItems.some((i) => i.status === STATUS.UPLOADING);

    return {
        items: useItems, total: TOTAL, done: DONE, isUploading: UPLOADING,
        progress: TOTAL ? Math.round((DONE / TOTAL) * 100) : 0,
        onAdd: CB__onAdd, onCancel: CB__onCancel, onRetry: CB__onRetry, onRemove: CB__onRemove, onClear: CB__onClear, onStartAll: CB__onStartAll,
    };
};
